import React from 'react';
import { trim } from 'ramda';
import { Form, Input, Icon, Modal } from 'antd';
import { Selection } from '../../common';
import { asserts } from '../../../utils';
import message from '../message';
import './index.less';

const { confirm } = Modal;
const { isExist } = asserts;

export const getCustomCodeSelectPopupContainer = (triggerNode) => {
  let node = triggerNode;
  while (node && node !== document.body) {
    if (node.classList && node.classList.contains('ant-popover')) {
      return node;
    }
    node = node.parentNode;
  }
  return document.body;
};

/** 高于 Popconfirm/Popover，避免删除确认框被遮挡 */
const CUSTOM_CODE_DELETE_CONFIRM_Z_INDEX = 1060;

/**
 * 关闭自定义编码悬浮框后弹出删除确认（Select 下拉内点击需延后，否则 confirm 不会出现）
 */
export const showCustomCodeDeleteConfirm = ({ uitemId, imitm, preferUitemCode, dispatch }) => {
  window.setTimeout(() => {
    confirm({
      title: '消息',
      content: '删除将不可恢复，确定要删除吗？',
      okText: '是',
      cancelText: '否',
      zIndex: CUSTOM_CODE_DELETE_CONFIRM_Z_INDEX,
      okButtonProps: { type: 'primary', className: 'btn-info' },
      onOk: () =>
        dispatch('deleteCustomCodeItem')({ uitemId }).then((result) => {
          if (result && isExist(imitm)) {
            return dispatch('queryCustom')({ imitm, preferUitemCode });
          }
        }),
    });
  }, 100);
};

/**
 * 商品列表/特价区/满减区等表格共用的「自定义编码」PopUp 表单逻辑
 */
export default function useCustomCodePop({
  form,
  dispatch,
  state,
  handleCustom,
  getImitmFromRecord,
  customCodeLoading = false,
}) {
  const { getFieldDecorator, getFieldValue, resetFields, setFields } = form;
  const {
    customCodeAddOrEdit,
    editingCustomCodeItem,
    newCustomCode,
    newCustomCodeList,
    customQueryImitm,
    key,
    customVisible,
  } = state;

  const suppressCustomPopCloseRef = React.useRef(false);
  const customCodeAddOrEditRef = React.useRef(false);
  const editingCustomCodeItemRef = React.useRef(null);

  React.useEffect(() => {
    customCodeAddOrEditRef.current = customCodeAddOrEdit;
    editingCustomCodeItemRef.current = editingCustomCodeItem;
    if (customCodeAddOrEdit) {
      suppressCustomPopCloseRef.current = true;
    }
  }, [customCodeAddOrEdit, editingCustomCodeItem]);

  // 切换为编辑输入态时 Popconfirm 内容重渲染，补一次保持打开
  React.useEffect(() => {
    if (customCodeAddOrEdit && key !== undefined && !customVisible) {
      dispatch('updateState')({ customVisible: true });
    }
  }, [customCodeAddOrEdit, key, customVisible, dispatch]);

  const resetCustomCodePopGuards = () => {
    suppressCustomPopCloseRef.current = false;
    customCodeAddOrEditRef.current = false;
    editingCustomCodeItemRef.current = null;
  };

  const resetCustomCodeFormState = () => {
    dispatch('updateState')({
      customCodeAddOrEdit: false,
      editingCustomCodeItem: null,
    });
  };

  const fetchCustomCodeList = (record) => {
    resetCustomCodePopGuards();
    const imitm = getImitmFromRecord(record);
    const preferUitemCode = record.userCode || null;
    dispatch('updateState')({
      customQueryImitm: imitm,
      newCustomCodeList: [],
      newCustomCode: preferUitemCode,
      customCodeListLoading: true,
      customCodeAddOrEdit: false,
      editingCustomCodeItem: null,
    });
    dispatch('queryCustom')({ imitm, preferUitemCode });
  };

  const refreshCustomCodeList = (record) => {
    fetchCustomCodeList(record);
  };

  const handleQueryCustomCode = (record) => {
    fetchCustomCodeList(record);
  };

  const onCustomVisibleChange = (record, index, visible) => {
    const shouldKeepOpen =
      suppressCustomPopCloseRef.current || customCodeAddOrEditRef.current;

    if (!visible && shouldKeepOpen) {
      if (index === key) {
        const patch = {
          customCode: record.customCode,
          key: index,
          isOperation: true,
          customVisible: true,
        };
        if (customCodeAddOrEditRef.current) {
          patch.customCodeAddOrEdit = true;
        }
        dispatch('updateState')(patch);
      }
      return;
    }
    if (visible) {
      dispatch('updateState')({
        customCode: record.customCode,
        key: index,
        isOperation: true,
        customVisible: true,
      });
      fetchCustomCodeList(record);
      return;
    }
    window.setTimeout(() => {
      if (customCodeAddOrEditRef.current || suppressCustomPopCloseRef.current) {
        return;
      }
      resetCustomCodeFormState();
      resetCustomCodePopGuards();
      dispatch('updateState')({
        customCode: record.customCode,
        key: index,
        isOperation: true,
        customVisible: false,
        newCustomCodeList: [],
        newCustomCode: null,
        customCodeListLoading: false,
      });
    }, 0);
  };

  const handleCustomConfirm = (record) => {
    const inEditMode = customCodeAddOrEditRef.current;
    const editingItem = editingCustomCodeItemRef.current;

    if (inEditMode) {
      const uitemCode = getFieldValue('customCode');
      if (!isExist(uitemCode) || !trim(uitemCode)) {
        message.warning('请输入自定义编码');
        return;
      }
      const trimmedCode = trim(uitemCode);
      const oldUitemCode =
        editingItem && isExist(editingItem.uitemCode) ? editingItem.uitemCode : '';
      handleCustom(record, trimmedCode, oldUitemCode);
      refreshCustomCodeList(record);
    } else {
      if (!isExist(newCustomCode)) {
        message.warning('请选择自定义编码');
        return;
      }
      const oldUitemCode = isExist(record.userCode) ? record.userCode : '';
      handleCustom(record, newCustomCode, oldUitemCode);
    }
    dispatch('updateState')({
      customVisible: false,
      customCode: '',
      newCustomCodeList: [],
      newCustomCode: null,
      customCodeListLoading: false,
    });
    resetCustomCodeFormState();
    resetCustomCodePopGuards();
    resetFields();
  };

  const handleCustomCancel = () => {
    resetCustomCodePopGuards();
    dispatch('updateState')({
      key: null,
      customVisible: false,
      customCode: '',
      newCustomCodeList: [],
      newCustomCode: null,
      customCodeListLoading: false,
    });
    resetCustomCodeFormState();
    resetFields();
  };

  const handleNewCustomCode = () => {
    resetFields(['customCode']);
    suppressCustomPopCloseRef.current = true;
    customCodeAddOrEditRef.current = true;
    editingCustomCodeItemRef.current = null;
    dispatch('updateState')({
      customCodeAddOrEdit: true,
      editingCustomCodeItem: null,
      customVisible: true,
    });
  };

  const stopSelectEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleEditCustomCodeItem = (item, e) => {
    stopSelectEvent(e);
    suppressCustomPopCloseRef.current = true;
    customCodeAddOrEditRef.current = true;
    editingCustomCodeItemRef.current = item;
    dispatch('updateState')({
      customCodeAddOrEdit: true,
      editingCustomCodeItem: item,
      customVisible: true,
    });
    requestAnimationFrame(() => {
      setFields({ customCode: { value: item.uitemCode } });
      dispatch('updateState')({ customVisible: true, customCodeAddOrEdit: true });
    });
  };

  const keepCustomPopOpen = () => {
    if (customCodeAddOrEditRef.current) {
      dispatch('updateState')({ customVisible: true });
    }
  };

  const handleDeleteCustomCodeItem = (item, e) => {
    stopSelectEvent(e);
    const uitemId = item.uitemId;
    if (!isExist(uitemId)) {
      message.warning('无法删除该编码');
      return;
    }
    const imitm = customQueryImitm;
    const preferUitemCode = newCustomCode;

    resetCustomCodeFormState();
    resetCustomCodePopGuards();
    dispatch('updateState')({ customVisible: false });

    showCustomCodeDeleteConfirm({ uitemId, imitm, preferUitemCode, dispatch });
  };

  const customCodeTransfer = { key: 'uitemId', code: 'uitemCode', name: 'uitemCode' };

  const renderCustomCodeOption = (Option, data) => {
    const value = data.uitemCode;
    return (
      <Option key={data.uitemId || value} value={value} label={value} className="custom-code-option">
        <span className="custom-code-option__label">{value}</span>
        <span className="custom-code-option__actions">
          <Icon
            type="edit"
            title="编辑"
            onMouseDown={(e) => handleEditCustomCodeItem(data, e)}
          />
          <Icon
            type="delete"
            title="删除"
            onMouseDown={(e) => handleDeleteCustomCodeItem(data, e)}
          />
        </span>
      </Option>
    );
  };

  const customTitle = (
    <Form
      className="customTitle custom-code-pop-form"
      onMouseDown={customCodeAddOrEdit ? stopSelectEvent : undefined}
      onClick={customCodeAddOrEdit ? stopSelectEvent : undefined}
    >
      {!customCodeAddOrEdit ? (
        <Form.Item label="自定义编码">
          <div
            onMouseDown={(e) => {
              e.preventDefault();
              return false;
            }}
          >
            <Selection
              source={newCustomCodeList}
              transfer={customCodeTransfer}
              style={{ width: 200 }}
              value={newCustomCode}
              loading={customCodeLoading}
              getPopupContainer={getCustomCodeSelectPopupContainer}
              optionLabelProp="label"
              dropdownClassName="custom-code-select-dropdown"
              optionRender={renderCustomCodeOption}
              onChange={(val) => dispatch('updateState')({ newCustomCode: val })}
              dropdownRender={(menu) => (
                <div>
                  {menu}
                  <div className="pointer text-red newFavorite" onClick={handleNewCustomCode}>
                    新增自定义编码
                  </div>
                </div>
              )}
            />
          </div>
        </Form.Item>
      ) : (
        <Form.Item
          label="自定义编码"
          key={editingCustomCodeItem ? `edit-${editingCustomCodeItem.uitemId}` : 'new-custom-code'}
        >
          <div onMouseDown={stopSelectEvent}>
            {getFieldDecorator('customCode', {
              initialValue: editingCustomCodeItem ? editingCustomCodeItem.uitemCode : undefined,
            })(
              <Input
                autoFocus
                onFocus={keepCustomPopOpen}
                onMouseDown={stopSelectEvent}
                onClick={stopSelectEvent}
              />,
            )}
          </div>
        </Form.Item>
      )}
    </Form>
  );

  return {
    customTitle,
    customPopOverlayClassName: 'custom-code-popover',
    customPopDestroyTooltipOnHide: false,
    handleQueryCustomCode,
    onCustomVisibleChange,
    handleCustomConfirm,
    handleCustomCancel,
  };
}
